import { z } from 'zod';

export const LogoSchema = z.object({
  storagePath: z.string().optional(),
  url: z.string().url().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
});

/**
 * Email provider options for compose links
 */
export const EmailProviderSchema = z.enum([
  'default',
  'gmail',
  'outlookWeb',
  'outlookLive',
  'yahoo',
]);
export type EmailProviderSetting = z.infer<typeof EmailProviderSchema>;

/**
 * Email settings for the team
 */
export const EmailSettingsSchema = z.object({
  /** Default email provider for compose links (Gmail, Outlook, etc.) */
  defaultProvider: EmailProviderSchema.default('default'),
  /** Default closing text for emails (e.g., "Met vriendelijke groet,") */
  closingText: z.string().max(500).optional(),
});

/**
 * Feature flags for the team
 * All flags default to true (enabled) if not set
 */
export const FeatureFlagsSchema = z.object({
  /** Enable Quick Calculator in TopBar (⌘L shortcut) */
  quickCalculatorEnabled: z.boolean().default(true),
  /** Enable Garage module (work orders, time tracking, planning board) */
  garageEnabled: z.boolean().default(true),
});

export type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;

/**
 * Invoice settings for the team
 * Default payment terms (due date calculation)
 */
export const InvoiceSettingsSchema = z.object({
  /** Default payment term in days for purchase invoices (vervaldatum = factuurdatum + X dagen) */
  defaultDueDaysPurchase: z.number().min(0).max(365).default(30),
  /** Default payment term in days for sales invoices (vervaldatum = factuurdatum + X dagen) */
  defaultDueDaysSales: z.number().min(0).max(365).default(14),
});

export type InvoiceSettings = z.infer<typeof InvoiceSettingsSchema>;

/** Default invoice settings */
export const DEFAULT_INVOICE_SETTINGS: InvoiceSettings = {
  defaultDueDaysPurchase: 30,
  defaultDueDaysSales: 14,
};

/**
 * Analytics settings for the team
 * Controls KPI trend calculations using period buckets
 */
export const AnalyticsSettingsSchema = z.object({
  /**
   * Trend period in days for KPI calculations.
   * - Big KPI values: computed from vehicles sold in [now - periodDays, now]
   * - % change: compares current period vs previous period of equal length
   * Allowed values: 30, 90. Default: 30.
   * - 30 = more responsive, noisier
   * - 90 = more stable
   */
  trendPeriodDays: z.union([z.literal(30), z.literal(90)]).default(30),
});

export type AnalyticsSettings = z.infer<typeof AnalyticsSettingsSchema>;

/** Default analytics settings */
export const DEFAULT_ANALYTICS_SETTINGS: AnalyticsSettings = {
  trendPeriodDays: 30,
};

/**
 * Work Schedule Settings
 * Controls working hours and workdays for Garage Planning and Agenda views
 */
export const WorkScheduleSchema = z.object({
  /** Working days (1=Mon, 2=Tue, ..., 7=Sun). Default Mon-Fri */
  workDays: z.array(z.number().min(1).max(7)).default([1, 2, 3, 4, 5]),
  /** Day start time in HH:mm format */
  dayStart: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .default('08:00'),
  /** Day end time in HH:mm format */
  dayEnd: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .default('17:00'),
  /** Time slot increment in minutes (for snapping) */
  slotMinutes: z.number().min(5).max(60).default(15),
  /** Agenda view: visible start hour (0-23). Default 8 */
  agendaDayStartHour: z.number().min(0).max(23).default(8),
  /** Agenda view: visible end hour (0-23). Default 18 */
  agendaDayEndHour: z.number().min(0).max(23).default(18),
});

export type WorkSchedule = z.infer<typeof WorkScheduleSchema>;

/** Default work schedule settings */
export const DEFAULT_WORK_SCHEDULE: WorkSchedule = {
  workDays: [1, 2, 3, 4, 5], // Mon-Fri
  dayStart: '08:00',
  dayEnd: '17:00',
  slotMinutes: 15,
  agendaDayStartHour: 8,
  agendaDayEndHour: 18,
};

/**
 * Dashboard Ring Settings
 * Controls thresholds for Whoop-style performance rings
 * Rings: Doorstroom (Velocity), Vandaag (Flow), Liquiditeit (Liquidity)
 */
export const DashboardRingSettingsSchema = z.object({
  /** Days after which a vehicle is considered "stale" (aging). Default: 30 */
  staleVehicleDays: z.number().min(7).max(180).default(30),
  /** Days after which a vehicle is considered "critical" (0% health). Default: 90 */
  criticalVehicleDays: z.number().min(14).max(365).default(90),
  /** Ideal days to collect payment after invoice sent. Default: 7 */
  idealCollectionDays: z.number().min(1).max(90).default(7),
  /** Days after quote sent before follow-up task is created. Default: 3 */
  quoteFollowUpDays: z.number().min(1).max(30).default(3),
  /** Business days (1=Mon, 2=Tue, ..., 7=Sun). Default Mon-Fri */
  businessDays: z.array(z.number().min(1).max(7)).default([1, 2, 3, 4, 5]),
});

export type DashboardRingSettingsType = z.infer<typeof DashboardRingSettingsSchema>;

/** Default dashboard ring settings */
export const DEFAULT_DASHBOARD_RING_SETTINGS: DashboardRingSettingsType = {
  staleVehicleDays: 30,
  criticalVehicleDays: 90,
  idealCollectionDays: 7,
  quoteFollowUpDays: 3,
  businessDays: [1, 2, 3, 4, 5], // Mon-Fri
};

/**
 * Sales Safeguards Settings
 * Controls warnings and confirmations during sales invoice creation.
 * All safeguards default to TRUE (enabled) for safety.
 */
export const SalesSafeguardsSchema = z.object({
  /** Warn when sales VAT regime differs from purchase VAT regime */
  vatRegimeMismatchWarning: z.boolean().default(true),
  /** Warn when sales mileage is lower than purchase mileage */
  mileageLowerThanPurchaseWarning: z.boolean().default(true),
  /** Require VIN before completing/exporting a sales invoice (for meldcode) */
  vinRequiredOnFinalize: z.boolean().default(true),
  /** Warn when sales date is before purchase date */
  salesDateBeforePurchaseWarning: z.boolean().default(true),
  /** Warn when selling below purchase price (disabled by default - less common) */
  priceBelowPurchaseWarning: z.boolean().default(false),
});

export type SalesSafeguards = z.infer<typeof SalesSafeguardsSchema>;

/** Default sales safeguards - all enabled except price warning */
export const DEFAULT_SALES_SAFEGUARDS: SalesSafeguards = {
  vatRegimeMismatchWarning: true,
  mileageLowerThanPurchaseWarning: true,
  vinRequiredOnFinalize: true,
  salesDateBeforePurchaseWarning: true,
  priceBelowPurchaseWarning: false,
};

export const AppSettingsSchema = z.object({
  teamId: z.string(),
  company: z
    .object({
      logo: LogoSchema.optional(),
      legalName: z.string().optional(),
      vatNumber: z.string().optional(),
      kvkNumber: z.string().optional(),
      address: z.string().optional(),
    })
    .partial(),
  // Email settings (provider preference)
  email: EmailSettingsSchema.optional(),
  // Feature flags (calculator, etc.)
  features: FeatureFlagsSchema.optional(),
  // Invoice settings (payment terms, etc.)
  invoices: InvoiceSettingsSchema.optional(),
  // Analytics settings (KPI trend period)
  analytics: AnalyticsSettingsSchema.optional(),
  // Work schedule settings (working hours, workdays)
  workSchedule: WorkScheduleSchema.optional(),
  // Dashboard ring settings (Whoop-style performance rings)
  dashboardRings: DashboardRingSettingsSchema.optional(),
  // Sales safeguards (warnings and confirmations during sales invoice creation)
  salesSafeguards: SalesSafeguardsSchema.optional(),
  // NOTE: invoicePdf field removed - all invoice PDF settings now live in PDF Templates
  audit: z
    .object({
      createdAt: z.any().optional(),
      updatedAt: z.any().optional(),
      createdBy: z.string().nullable().optional(),
      updatedBy: z.string().nullable().optional(),
    })
    .partial(),
});

export type AppSettings = z.infer<typeof AppSettingsSchema>;

export const defaultSettings = (teamId: string): AppSettings => ({
  teamId,
  company: {
    logo: undefined,
    legalName: '',
    vatNumber: '',
    kvkNumber: '',
    address: '',
  },
  email: {
    defaultProvider: 'default',
  },
  features: {
    quickCalculatorEnabled: true,
    garageEnabled: true,
  },
  invoices: {
    defaultDueDaysPurchase: DEFAULT_INVOICE_SETTINGS.defaultDueDaysPurchase,
    defaultDueDaysSales: DEFAULT_INVOICE_SETTINGS.defaultDueDaysSales,
  },
  analytics: {
    trendPeriodDays: DEFAULT_ANALYTICS_SETTINGS.trendPeriodDays,
  },
  workSchedule: {
    ...DEFAULT_WORK_SCHEDULE,
  },
  dashboardRings: {
    ...DEFAULT_DASHBOARD_RING_SETTINGS,
  },
  salesSafeguards: {
    ...DEFAULT_SALES_SAFEGUARDS,
  },
  audit: {},
});
